import { useState, useEffect } from "react";
import { AIChat } from "./AIChat";
import { Sparkles, X, MessageSquareText } from "lucide-react";

/**
 * Google-Cloud-Assist style collapsible side drawer.
 * Mounted globally on Ops + Portal layouts.
 */
export function AISidePanel({ mode = "customer", intro, quickActions }) {
  const [open, setOpen] = useState(false);

  // Persist state so it stays open across navigation
  useEffect(() => {
    const saved = sessionStorage.getItem(`ai-panel-${mode}`);
    if (saved === "1") setOpen(true);
  }, [mode]);
  useEffect(() => { sessionStorage.setItem(`ai-panel-${mode}`, open ? "1" : "0"); }, [open, mode]);

  return (
    <>
      {/* Floating FAB */}
      {!open && (
        <button
          data-testid="ai-panel-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full pl-4 pr-5 h-14 bg-[var(--cc-accent)] text-black font-bold btn-glow shadow-2xl hover:scale-105 transition-transform duration-200"
          aria-label="Open AI Assist"
        >
          <Sparkles className="w-5 h-5" />
          <span className="hidden sm:inline text-sm">Ask AI</span>
        </button>
      )}

      {/* Backdrop on mobile */}
      {open && (
        <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] lg:hidden" onClick={() => setOpen(false)} data-testid="ai-panel-backdrop" />
      )}

      {/* Drawer */}
      <aside
        data-testid="ai-panel"
        aria-hidden={!open}
        className={`fixed top-0 right-0 h-screen z-50 flex flex-col bg-background border-l shadow-2xl transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        } w-full sm:w-[420px]`}
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="flex items-center justify-between h-14 px-4 border-b bg-card">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-[var(--cc-accent)]/15 border border-[var(--cc-accent)]/40 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[var(--cc-accent)]" />
            </span>
            <div className="min-w-0">
              <p className="font-heading font-bold text-sm">CastleCorp Assist</p>
              <p className="text-[10px] font-mono-cc uppercase tracking-widest text-muted-foreground">
                {mode === "admin" ? "Operations mode" : "Customer support"}
              </p>
            </div>
          </div>
          <button data-testid="ai-panel-close" onClick={() => setOpen(false)}
            className="w-9 h-9 rounded-full hover:bg-secondary flex items-center justify-center transition-colors"
            aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <AIChat mode={mode} intro={intro} quickActions={quickActions} className="flex-1 min-h-0" />
      </aside>
    </>
  );
}
